import { mockResponse, request, isMockMode } from './client.js';
import { THEMES } from '../data/themes.js';

/**
 * 主題圖鑑 API（介紹型，非購買）。
 * 後端完成後，用 REACT_APP_USE_MOCK_THEMES=false 單獨切真後端。
 *
 * mock 階段用可變副本 _store，讓後台 CRUD 在 session 內生效（重整 reset）。
 */

// 補上 mock 資料可能沒帶的欄位，讓後台列表 / 表單有完整結構
let _store = THEMES.map(t => ({
  active: true, displayOrder: 0, images: [], videoUrl: '',
  availability: 'permanent', availableFrom: null, availableUntil: null,
  availableNote: '', endedNote: '',
  ...t,
}));

// ─── 前台讀取 ───────────────────────────────────────────────
// 公開列表（分頁）。回傳 PageResponse 形狀：{ items, total, page, limit, totalPages }
// @param type  選填，'d2'|'d3'|'fun'|'award'
// @param q     選填，關鍵字（比對 名稱 / 來源 / 參考資料）
// @param page  頁碼（從 1 起算），預設 1
// @param limit 每頁筆數，預設 12
export function getThemes({ type, q, page = 1, limit = 12, sort } = {}) {
  if (!isMockMode('themes')) {
    const params = new URLSearchParams();
    if (type) params.set('type', type);
    if (q) params.set('q', q);
    params.set('page', page);
    params.set('limit', limit);
    if (sort) params.set('sort', sort);
    return request(`/api/themes?${params.toString()}`);
  }
  return mockResponse(() => {
    const kw = (q || '').trim().toLowerCase();
    // 沒填 acquiredDate 的排最後（nullsLast）
    const byAcquired = (dir) => (a, b) => {
      const av = a.acquiredDate, bv = b.acquiredDate;
      if (!av && !bv) return Number(a.id) - Number(b.id);
      if (!av) return 1;
      if (!bv) return -1;
      return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    };
    const sorter = sort === 'acquired_desc' ? byAcquired('desc')
      : sort === 'acquired_asc' ? byAcquired('asc')
      : (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0) || (Number(a.id) - Number(b.id));
    const all = _store
      .filter(t => t.active !== false)
      .filter(t => !type || t.type === type)
      .filter(t => !kw || [t.name, t.source, t.story].some(f => String(f || '').toLowerCase().includes(kw)))
      .sort(sorter);
    const total = all.length;
    const start = (page - 1) * limit;
    return {
      items: all.slice(start, start + limit),
      total,
      page,
      limit,
      totalPages: limit > 0 ? Math.ceil(total / limit) : 0,
    };
  });
}

// 前台分類計數（tab 徽章用）：{ all, d2, d3, fun, award }
export function getThemeCounts() {
  if (!isMockMode('themes')) return request('/api/themes/counts');
  return mockResponse(() => {
    const active = _store.filter(t => t.active !== false);
    const counts = { all: active.length };
    for (const k of ['d2', 'd3', 'fun', 'award']) {
      counts[k] = active.filter(t => t.type === k).length;
    }
    return counts;
  });
}

export function getTheme(id) {
  if (!isMockMode('themes')) return request(`/api/themes/${id}`);
  return mockResponse(() => _store.find(t => String(t.id) === String(id) && t.active !== false) || null);
}

// ─── 後台 CRUD ──────────────────────────────────────────────

// 後台列表：含未上架（active=false）。分頁 + 篩選(type/q/status) + 排序(sort="欄位,方向")
export function adminGetThemes({ type, q, status, sort, page = 1, limit = 20 } = {}) {
  if (!isMockMode('themes')) {
    const params = new URLSearchParams();
    if (type && type !== 'all') params.set('type', type);
    if (q) params.set('q', q);
    if (status) params.set('status', status);
    if (sort) params.set('sort', sort);
    params.set('page', page);
    params.set('limit', limit);
    return request(`/api/admin/themes?${params.toString()}`);
  }
  return mockResponse(() => {
    const kw = (q || '').trim().toLowerCase();
    const [sf, sd] = (sort || '').split(',');
    const cmp = (a, b) => {
      if (sf === 'acquiredDate') {
        const av = a.acquiredDate, bv = b.acquiredDate;
        if (!av && !bv) return Number(a.id) - Number(b.id);
        if (!av) return 1; if (!bv) return -1;
        return sd === 'desc' ? bv.localeCompare(av) : av.localeCompare(bv);
      }
      if (sf && a[sf] != null && b[sf] != null) {
        const r = String(a[sf]).localeCompare(String(b[sf]), 'zh-Hant', { numeric: true });
        return sd === 'desc' ? -r : r;
      }
      return (a.displayOrder || 0) - (b.displayOrder || 0) || (Number(a.id) - Number(b.id));
    };
    const all = _store
      .filter(t => !type || type === 'all' || t.type === type)
      .filter(t => !kw || [t.name, t.source].some(f => String(f || '').toLowerCase().includes(kw)))
      .filter(t => !status || (status === 'active' ? t.active !== false : status === 'hidden' ? t.active === false : true))
      .sort(cmp);
    const total = all.length;
    const start = (page - 1) * limit;
    return { items: all.slice(start, start + limit), total, page, limit, totalPages: limit > 0 ? Math.ceil(total / limit) : 0 };
  });
}

export function adminGetTheme(id) {
  if (!isMockMode('themes')) return request(`/api/admin/themes/${id}`);
  return mockResponse(() => _store.find(t => String(t.id) === String(id)) || null);
}

export function adminCreateTheme(data) {
  if (!isMockMode('themes')) return request('/api/admin/themes', { method: 'POST', body: data });
  return mockResponse(() => {
    const id = Math.max(100000, ..._store.map(t => Number(t.id) || 0)) + 1;
    const created = {
      images: [], videoUrl: '', story: '', source: '', displayOrder: 0, active: true,
      availability: 'permanent', availableFrom: null, availableUntil: null,
      availableNote: '', endedNote: '',
      ...data, id,
    };
    _store = [..._store, created];
    return created;
  });
}

export function adminUpdateTheme(id, patch) {
  if (!isMockMode('themes')) return request(`/api/admin/themes/${id}`, { method: 'PATCH', body: patch });
  return mockResponse(() => {
    const idx = _store.findIndex(t => String(t.id) === String(id));
    if (idx === -1) throw new Error(`找不到主題：${id}`);
    const updated = { ..._store[idx], ...patch };
    _store = [..._store.slice(0, idx), updated, ..._store.slice(idx + 1)];
    return updated;
  });
}

export function adminDeleteTheme(id) {
  if (!isMockMode('themes')) return request(`/api/admin/themes/${id}`, { method: 'DELETE' });
  return mockResponse(() => {
    _store = _store.filter(t => String(t.id) !== String(id));
    return { ok: true };
  });
}
